import React from 'react'
import { reaction } from 'mobx'
import { Container, Modal, Button } from 'react-bootstrap'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { GrClose } from 'react-icons/gr'
import {
  i18nTranslate,
  application,
  trackGTMEvents,
  getMonthStringList,
  setLocalStorage,
  createFocusTrap,
} from 'src/utils'
import { imagePlaceholder } from 'src/static/fixtures/imagePlaceholder'
import {
  LinkWithLocale,
  ImageComponent,
  Quantity,
  DatePickerV2,
} from 'src/views/components'
import { FrequencyDropdownV2 } from 'src/views/components/FrequencyDropdownV2'
import { ProductDetails } from '../ProductDetails'
import {
  productDeps,
  getLastDateOfCurrentMonth,
  constructDateV2,
  isToShowTwoMonthCalender,
  isPbomProduct,
} from 'src/deps'
import { promotionContainer, subscriptionContainer } from 'src/models'
import BundleIncludedItemsCollapse from '../SubscriptionNextOrders/BundleIncludedItemsCollapse'
import ScanInfoModal from '../ScanInfoModal'
import { SKIP_DELETE_MODAL_TYPES } from 'src/static/fixtures/flexibleSubscription'
import { RiDeleteBinLine as DeleteIcon } from 'react-icons/ri'

import './styles.scss'
@observer
/**
 * SubscriptionModal component renders a modal
 * with product details.
 *
 * Props:
 * - show: Boolean to show/hide modal
 * - onHide: Callback to hide modal
 * - productData: Object with product data
 */
class SubscriptionModal extends React.Component {
  @observable quantity = 0
  @observable processingDate = new Date().getDate()
  @observable isOneTime = false
  @observable frequencyData = {
    frequencyPeriod: 1,
    frequencyType: 'Every month',
    type: 'VARIABLE_MULTI_ORDER',
  }
  @observable selectedSubscriptionFullDate = new Date()
  @observable productDetails = {}
  @observable isApplyButtonDisabled = true
  @observable selectedProcessingDate = new Date().getDate()
  @observable isInfoModalEnabled = false
  @observable infoModalType = ''
  @observable isFrequencyChanged = false
  @observable isQuantityChanged = false
  @observable isDateChanged = false
  focusTrap = {}
  currentProcessingDate = new Date().getDate()

  constructor(props) {
    super(props)
    const {
      productData,
      data,
      product,
      nonProcessedFutureSubscriptionDates,
      availableMinDate,
    } = this.props
    const { processingDate, isOneTime = false } = productData
    this.isOneTime = isOneTime
    const { nextOrderCreationDate } = data
    let nextOrderDate = nextOrderCreationDate
      ? new Date(nextOrderCreationDate)
      : availableMinDate
    this.frequencyData = {
      frequencyPeriod: this.props?.frequencyPeriod || 1,
      frequencyType: this.props?.frequencyType || 'Every month',
      type: this.props?.productData?.type || 'VARIABLE_MULTI_ORDER',
    }
    this.selectedProcessingDate = processingDate
    // const tempProcessDate =
    //   this.isOneTime && nonProcessedFutureSubscriptionDates.length > 0
    //     ? new Date(nonProcessedFutureSubscriptionDates?.[0])
    //     : availableMinDate
    // Set the processing date to the selected date
    this.selectedSubscriptionFullDate = constructDateV2(
      nextOrderDate.getFullYear(),
      nextOrderDate.getMonth(),
      processingDate
    )
    this.currentProcessingDate = constructDateV2(
      nextOrderDate.getFullYear(),
      nextOrderDate.getMonth(),
      processingDate
    )

    reaction(
      () => ({
        frequencyPeriod: this.frequencyData?.frequencyPeriod,
        frequencyType: this.frequencyData?.frequencyType,
        type: this.frequencyData?.type,
        selectedProcessingDate: this.selectedProcessingDate,
        quantity: this.productDetails?.quantity,
        isOneTime: this.isOneTime,
        selectedSubscriptionFullDate: this.selectedSubscriptionFullDate,
      }),
      data => {
        this.isApplyButtonDisabled =
          data.frequencyPeriod?.toString() ===
            this.props?.frequencyPeriod?.toString() &&
          data.frequencyType === this.props?.frequencyType &&
          data.type === this.props?.productData?.type &&
          data.selectedProcessingDate?.toString() ===
            this.props.productData?.processingDate?.toString() &&
          data.quantity?.toString() ===
            this.props.productData?.quantity?.toString() &&
          data.isOneTime === this.props.productData?.isOneTime &&
          data.selectedSubscriptionFullDate?.getMonth() ===
            this.currentProcessingDate?.getMonth()

        this.isQuantityChanged =
          data.quantity?.toString() !==
          this.props.productData?.quantity?.toString()
        this.isDateChanged =
          data.selectedProcessingDate?.toString() !==
            this.props.productData?.processingDate?.toString() ||
          (data.selectedSubscriptionFullDate?.getDate() ===
            this.currentProcessingDate?.getDate() &&
            data.selectedSubscriptionFullDate?.getMonth() !==
              this.currentProcessingDate?.getMonth())
        this.isFrequencyChanged =
          data.frequencyPeriod?.toString() !==
            this.props?.frequencyPeriod?.toString() ||
          data.frequencyType !== this.props?.frequencyType ||
          data.type !== this.props?.productData?.type

        if (data.isOneTime) {
          this.isApplyButtonDisabled = !(
            this.props.nonProcessedFutureSubscriptionDates?.length > 0
          )
        }
      }
    )
  }

  componentWillUnmount() {
    this.focusTrap?.disable()
  }

  componentDidMount() {
    this.focusTrap = createFocusTrap('.modal-dialog')
    this.focusTrap?.enable()

    const isBundle = productDeps.isBundleData({
      type: this.props.data?.itemInfo?.[0]?.properties?.producttype || '',
    })
    this.productDetails = {
      skuId: this.props.data?.itemInfo?.[0]?.skus?.[0]?.id,
      productId: this.props.data?.itemInfo?.[0]?.skus?.[0]?.productId,
      isSubscriptionIncludedInPromotion: true,
      isSubscriptionPromotion: true,
      quantity: this.quantity || this.props.productData?.quantity || 1,
    }

    if (isBundle) {
      this.productDetails = {
        ...this.productDetails,
        categoryId: this.props.data?.properties?.categoryId || '',
        type: this.props.data?.itemInfo?.[0]?.properties?.producttype || '',
        skus: this.props.data?.itemInfo?.[0]?.skus?.map(
          ({ id, productId, type, optionalQuantity = 0, skuQuantity = 0 }) => {
            const isOptional = type?.toLowerCase() === 'optional'
            return {
              skuId: id,
              productId,
              [isOptional ? 'optionalSkuQuantity' : 'skuQuantity']: (isOptional
                ? optionalQuantity
                : skuQuantity
              ).toString(),
            }
          }
        ),
      }
    }

    promotionContainer.makeSubscriptionPromoAPI(this.productDetails)
  }
  toggleScanInfoModal = (infoModalType = '') => {
    this.infoModalType = infoModalType
    this.isInfoModalEnabled = !this.isInfoModalEnabled
  }
  checkIfCanChangeFrequencyToOneTime = () => {
    if (this.props.canChangeFrequencyToOneTime) {
      return true
    } else {
      this.toggleScanInfoModal(SKIP_DELETE_MODAL_TYPES.FREQUENCY_CHANGE)
      return false
    }
  }
  handleSubscriptionFrequency = (value, isOneTime) => {
    this.isOneTime = isOneTime
    let available = false
    let isToMakePromoAPI = false
    let type = isOneTime ? 'VARIABLE_SINGLE_ORDER' : 'VARIABLE_MULTI_ORDER'
    if (this.frequencyData.type !== type) {
      isToMakePromoAPI = true
    }
    this.isOneTime = isOneTime
    const { nonProcessedFutureSubscriptionDates, availableMinDate = '' } =
      this.props
    this.productDetails.isSubscriptionIncludedInPromotion = !isOneTime
    if (nonProcessedFutureSubscriptionDates?.length > 0) {
      available = nonProcessedFutureSubscriptionDates?.some(
        processedDate =>
          processedDate?.getDate() ===
            this.selectedSubscriptionFullDate?.getDate() &&
          processedDate?.getMonth() ===
            this.selectedSubscriptionFullDate?.getMonth() &&
          processedDate?.getFullYear() ===
            this.selectedSubscriptionFullDate?.getFullYear()
      )
    }
    isToMakePromoAPI &&
      promotionContainer.makeSubscriptionPromoAPI(this.productDetails)
    if (!available && isOneTime) {
      // this.isSwitchedFromOnetime = true
      let date = nonProcessedFutureSubscriptionDates?.[0] || availableMinDate
      if (
        nonProcessedFutureSubscriptionDates?.[0]?.getDate() === 1 &&
        nonProcessedFutureSubscriptionDates?.length > 1
      ) {
        date = nonProcessedFutureSubscriptionDates?.[1]
      } else if (
        nonProcessedFutureSubscriptionDates?.[0]?.getDate() === 1 &&
        nonProcessedFutureSubscriptionDates?.length === 1
      ) {
        date = availableMinDate
      }
      this.selectedSubscriptionFullDate = date
    }
    // this.selectedSubscriptionFullDate =
    // isOneTime && nonProcessedFutureSubscriptionDates.length > 0
    //   ? new Date(nonProcessedFutureSubscriptionDates?.[0])
    //   : this.selectedSubscriptionFullDate

    this.selectedProcessingDate = new Date(
      this.selectedSubscriptionFullDate
    ).getDate()
    const [frequencyPeriod, frequencyType] = value?.split?.('-')
    this.frequencyData = {
      frequencyPeriod,
      frequencyType,
      type: isOneTime ? 'VARIABLE_SINGLE_ORDER' : 'VARIABLE_MULTI_ORDER',
    }
  }
  handleChangeCalendar = timeStamp => {
    this.selectedSubscriptionFullDate = new Date(timeStamp)
    this.selectedProcessingDate = new Date(timeStamp).getDate()
  }
  handleManageOrder = async () => {
    let isFrom = ''
    if (
      this.isQuantityChanged &&
      this.isDateChanged &&
      this.isFrequencyChanged
    ) {
      isFrom = 'all'
    } else if (this.isQuantityChanged && this.isDateChanged) {
      isFrom = 'quantityAndDate'
    } else if (this.isQuantityChanged && this.isFrequencyChanged) {
      isFrom = 'quantityAndFrequency'
    } else if (this.isDateChanged && this.isFrequencyChanged) {
      isFrom = 'dateAndFrequency'
    } else if (this.isQuantityChanged) {
      isFrom = 'quantityChange'
    } else if (this.isDateChanged) {
      isFrom = 'dateChange'
    } else if (this.isFrequencyChanged) {
      // if (
      //   this.isOneTime &&
      //   !this.checkIfCanChangeFrequencyToOneTime(
      //     SKIP_DELETE_MODAL_TYPES.FREQUENCY_CHANGE
      //   )
      // ) {
      //   return false
      // }
      isFrom = 'frequencyChange'
    }
    const { handleProductEdit, data, onHide, productData = {} } = this.props
    const {
      priceObj = {},
      quantity = '',
      frequencyLabel = '',
      processingDate = '',
    } = productData || {}
    const month = this.selectedSubscriptionFullDate?.getMonth() + 1 || 1

    let response = await this.props.handleProductEdit(
      data,
      this.frequencyData,
      this.quantity,
      this.isOneTime,
      this.selectedProcessingDate,
      false,
      false,
      isFrom,
      month
    )

    trackGTMEvents({
      event: 'subscriptionManage',
      item_id: data?.id || '',
      item_Name: data?.item?.itemInfo?.sku?.skuInfo?.name || '',
      discount: priceObj?.totalValue?.discount || 0,
      total: priceObj?.totalValue?.priceAfterDiscount || 0,
      quantity: quantity || 1,
      frequency: frequencyLabel || '',
      processingDay: processingDate || '',
    })
    onHide && onHide()
    if (this.props?.activeTab === 'schedule') {
      let monthName = getMonthStringList()[month - 1]
      let selectedDate = `${monthName} ${this.selectedProcessingDate}`
      setLocalStorage('scheduleOrderSelectedDate', selectedDate)
      this.props.scrollToSelectedDate()
    }
  }
  handleQuantityChange = count => {
    this.quantity = count
    this.productDetails.quantity = count
  }
  datePreix = disDate => {
    let date = disDate?.getDate()
    const label =
      date % 10 == 1 && date != 11
        ? 'st'
        : date % 10 == 2 && date != 12
        ? 'nd'
        : date % 10 == 3 && date != 13
        ? 'rd'
        : 'th'
    return label
  }

  renderDatePicker = () => {
    const { productData } = this.props
    const { isOneTime } = productData
    const {
      nonProcessedPastSubscriptionDates = [],
      nonProcessedFutureSubscriptionDates = [],
      availableMinDate = '',
      maxDate = '',
      minDate = '',
      alreadyProcessedOrderDates = [],
      availableOrderDates = [],
      isToDisableCurrentMonthDatesInNextMonth = false,
      isToShowTwoMonthProcessingDates = false,
    } = this.props

    const isToEnableNextMonth = isToShowTwoMonthProcessingDates
    const currentMonthLastDate = getLastDateOfCurrentMonth(minDate)
    const availableMaxDate = isToEnableNextMonth
      ? maxDate
      : currentMonthLastDate

    return (
      <DatePickerV2
        minDate={minDate}
        maxDate={availableMaxDate}
        includeDateOnly={
          this.isOneTime && nonProcessedFutureSubscriptionDates.length
            ? nonProcessedFutureSubscriptionDates
            : []
        }
        highlightDates={nonProcessedFutureSubscriptionDates}
        selectedDateCalendar={this.selectedSubscriptionFullDate}
        handleChangeCalendar={this.handleChangeCalendar}
        subscriptionProcessedDates={alreadyProcessedOrderDates}
        isOneTime={this.isOneTime}
        nonProcessedPastSubscriptionDates={nonProcessedPastSubscriptionDates}
        availableOrderDates={availableOrderDates}
        disableShowNextMonth={isToShowTwoMonthProcessingDates}
      />
    )
  }

  render() {
    const { productData, data, nonProcessedFutureSubscriptionDates } =
      this.props
    const { timeZone = '' } =
      subscriptionContainer.subscriptionPreferenceResponse || {}
    const { nextOrderCreationDate } = data
    let disDate = nextOrderCreationDate
      ? new Date(nextOrderCreationDate)
      : new Date()
    const { pathUrl, productName, productImage, isOneTime, processingDate } =
      productData
    const displayDate = disDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timeZone || 'US/Mountain',
    })
    const currencySign =
      subscriptionContainer.subscriptionPreferenceResponse?.currencySign || '$'
    const unitValue =
      promotionContainer.subscriptionV2PromoResponse?.unitValue ||
      productData?.priceObj?.totalValue ||
      {}
    const totalValue =
      promotionContainer.subscriptionV2PromoResponse?.totalValue ||
      productData?.priceObj?.totalValue ||
      {}
    const { originalPrice = '', priceAfterDiscount = '' } = unitValue
    const { priceFacets = {} } = totalValue
    const priceObj = {
      currencySymbol: currencySign,
      price: parseFloat(originalPrice),
      discountedPrice: parseFloat(priceAfterDiscount),
      unitValue: unitValue,
      // totalValue: totalValue,
      totalValue: {
        originalPrice: parseFloat(originalPrice),
        priceAfterDiscount: parseFloat(priceAfterDiscount),
        priceFacets: priceFacets,
      },
    }
    const details = {
      ...productData,
      priceObj,
      subTotal: totalValue?.priceAfterDiscount || '',
      isOneTime: this.isOneTime,
    }
    let bundleItems = {}
    let includedItems = []
    const isBundle =
      productDeps.isBundleData({
        type: this.props.data?.itemInfo?.[0]?.properties?.producttype || '',
      }) || false
    if (isBundle) {
      bundleItems = subscriptionContainer.getBundleData(this.props.data) || {}
      includedItems = [
        ...bundleItems?.mandatoryCount,
        ...bundleItems?.optionalCount,
      ]
    }

    const skuDetails = isBundle
      ? bundleItems?.bundle?.[0]?.skuProperties
      : data?.itemInfo?.[0]?.skus?.[0]?.skuProperties

    const isPbomSkukit = isPbomProduct(
      skuDetails,
      bundleItems?.bundle?.[0]?.type
    )

    const isDisableCalender =
      nonProcessedFutureSubscriptionDates.length === 0 && this.isOneTime
    const isToShowTwoMonth = isToShowTwoMonthCalender() || false
    return (
      <>
        <Modal
          className="single-subscription-pop"
          data-testid="qa-subscription-product-overlay"
          show={this.props.show}
          onHide={() => this.props.onHide()}
          centered>
          <Modal.Body className="ssm-modal-body">
            <Container>
              <div>
                <button
                  className="text-right ssm-close-icon"
                  tabIndex={0}
                  data-testid="qa-edit-model-cancel-button"
                  onClick={() => this.props.onHide()}>
                  <GrClose />
                </button>
                <div className="d-flex">
                  <div className="product-list-product-img">
                    <LinkWithLocale
                      to={{
                        pathname: pathUrl || '',
                      }}
                      className={`prod-image-wrapper w-100`}>
                      <ImageComponent
                        width="91px"
                        height="91px"
                        optimizeImage={true}
                        src={productImage || imagePlaceholder}
                        alt={productName}
                        className={`mw-100 mh-100 prod-img`}
                        data-testid="qa-user-sub-product-image"
                      />
                    </LinkWithLocale>
                  </div>
                  <div className="w-100">
                    <div className="single-pop-tittle">
                      {i18nTranslate(
                        'subscription.subscriptionItemLabel',
                        'Subscription item'
                      )}
                    </div>
                    <ProductDetails productData={details} isFromModel={true} />
                    {!application.isMobile && (
                      <>
                        <div className="d-flex alignt-items-center quantity-section">
                          <span className="d-flex alignt-items-center quantity-text pr-2">
                            {i18nTranslate('quantity', 'Quantity')}
                          </span>
                          <Quantity
                            onQuantityChange={this.handleQuantityChange}
                            defaultValue={
                              this.quantity ||
                              this.props.productData?.quantity ||
                              1
                            }
                            isV2QuantityStyle={true}
                            handleQuantityChangePromotion={
                              promotionContainer.makeSubscriptionPromoAPI
                            }
                            product={this.productDetails}
                          />
                        </div>
                        <div
                          className={`subscription-frequency-wrapper mt-3 ${
                            !isToShowTwoMonth && 'disabled'
                          }`}>
                          <FrequencyDropdownV2
                            title={i18nTranslate('cart.frequency', 'Frequency')}
                            frequencyPeriod={
                              this.frequencyData?.frequencyPeriod
                            }
                            frequencyType={this.frequencyData?.frequencyType}
                            orderType={this.frequencyData?.type}
                            handleSubscriptionFrequency={
                              this.handleSubscriptionFrequency
                            }
                          />
                          {/* <FrequencyDropdownV2
                    title={i18nTranslate('cart.frequency', 'Frequency')}
                    frequencyPeriod={this.props?.frequencyPeriod}
                    frequencyType={this.props?.frequencyType}
                    orderType={this.props.productData?.type}
                  /> */}
                        </div>
                        <div className="subscription-Processing-wrapper mt-3">
                          <div className="subscription-Processing mb-2">
                            {this.isOneTime &&
                            nonProcessedFutureSubscriptionDates.length === 0 ? (
                              <div className="text-danger no-processing-text">
                                {i18nTranslate(
                                  'subscription.noDate',
                                  'No available Date for one time order'
                                )}
                              </div>
                            ) : (
                              <>
                                <span className="processing-text pr-1">
                                  {i18nTranslate(
                                    'subscription.nextProcessesOn',
                                    'Next processes on'
                                  )}
                                </span>
                                <span
                                  className="processing-text-date"
                                  data-testid="qa-product-processing-date">
                                  {displayDate}
                                  {this.datePreix(disDate)}
                                </span>
                              </>
                            )}
                          </div>
                          <div className={`${isDisableCalender && 'disabled'}`}>
                            {this.renderDatePicker()}
                          </div>
                          {isBundle && !isPbomSkukit && (
                            <BundleIncludedItemsCollapse
                              includedItems={includedItems}
                              customWrapperClass="subs-modal-bundle-items-wrapper"
                            />
                          )}
                        </div>
                        <Button
                          className="apply-change-button mt-4 p-0 px-3 d-flex align-items-center justify-content-center"
                          variant="outline-primary"
                          onClick={this.handleManageOrder}
                          disabled={this.isApplyButtonDisabled}
                          data-testid="apply-change-button">
                          {i18nTranslate(
                            'editShippingAddress.applyChanges',
                            'Apply Changes'
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {application.isMobile && (
                  <div className="mobile-subscription-wrapper">
                    <div className="d-flex alignt-items-center quantity-section">
                      <span className="d-flex alignt-items-center quantity-text pr-2">
                        {i18nTranslate('quantity', 'Quantity')}
                      </span>
                      <Quantity
                        onQuantityChange={this.handleQuantityChange}
                        defaultValue={
                          this.quantity || this.props.productData?.quantity || 1
                        }
                        isV2QuantityStyle={true}
                        handleQuantityChangePromotion={
                          promotionContainer.makeSubscriptionPromoAPI
                        }
                        product={this.productDetails}
                      />
                    </div>
                    <div
                      className={`subscription-frequency-wrapper mt-3 ${
                        !isToShowTwoMonth && 'disabled'
                      }`}>
                      <FrequencyDropdownV2
                        title={i18nTranslate('cart.frequency', 'Frequency')}
                        frequencyPeriod={this.frequencyData?.frequencyPeriod}
                        frequencyType={this.frequencyData?.frequencyType}
                        orderType={this.frequencyData?.type}
                        handleSubscriptionFrequency={
                          this.handleSubscriptionFrequency
                        }
                      />
                      {/* <FrequencyDropdownV2
                    title={i18nTranslate('cart.frequency', 'Frequency')}
                    frequencyPeriod={this.props?.frequencyPeriod}
                    frequencyType={this.props?.frequencyType}
                    orderType={this.props.productData?.type}
                  /> */}
                    </div>
                    <div className="subscription-Processing-wrapper mt-3">
                      <div className="subscription-Processing mb-2">
                        <span className="processing-text pr-1">
                          {i18nTranslate(
                            'subscription.nextProcessesOn',
                            'Next processes on'
                          )}
                        </span>
                        <span
                          className="processing-text-date"
                          data-testid="qa-product-processing-date">
                          {displayDate}
                          {this.datePreix(disDate)}
                        </span>
                      </div>
                      {this.renderDatePicker()}
                      {isBundle && !isPbomSkukit && (
                        <BundleIncludedItemsCollapse
                          includedItems={includedItems}
                          customWrapperClass="subs-modal-bundle-items-wrapper"
                        />
                      )}
                    </div>
                    <Button
                      className="apply-change-button mt-4 p-0 px-3 d-flex align-items-center justify-content-center"
                      variant="outline-primary"
                      onClick={this.handleManageOrder}
                      data-testid="apply-change-button">
                      {i18nTranslate(
                        'editShippingAddress.applyChanges',
                        'Apply Changes'
                      )}
                    </Button>
                  </div>
                )}
                <Button
                  className="edit-delete-button"
                  variant="black"
                  data-testid={'qa-user-subs-product-delete'}
                  onClick={this.props.handleDeleteItem}>
                  <DeleteIcon aria-label={'product delete icon'} />
                </Button>
              </div>
            </Container>
          </Modal.Body>
        </Modal>
        <ScanInfoModal
          infoModalType={this.infoModalType}
          isInfoModalEnabled={this.isInfoModalEnabled}
          toggleScanInfoModal={this.toggleScanInfoModal}
        />
      </>
    )
  }
}

export { SubscriptionModal }
export default SubscriptionModal
